import { Calendar as AntdCalendar } from 'antd'
import type { CalendarProps as AntdCalendarProps } from 'antd'
import {
  addDays,
  addMonths,
  addYears,
  endOfMonth,
  formatDate,
  getDate,
  getDay,
  getHours,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  getWeek,
  isValid,
  setDate,
  setHours,
  setMilliseconds,
  setMinutes,
  setMonth,
  setSeconds,
  setYear,
  startOfWeek,
  type Day
} from 'date-fns'
import { ja } from 'date-fns/locale'

function localeParse(format: string) {
  return format
    .replace(/Y/g, 'y')
    .replace(/D/g, 'd')
    .replace(/gggg/, 'yyyy')
    .replace(/g/g, 'G')
    .replace(/([Ww])o/g, 'wo')
}

// date-fns ベースの Calendar を生成
const DateFnsCalendar = AntdCalendar.generateCalendar<Date>({
  getNow: () => new Date(),
  getFixedDate: (string) => new Date(string),
  getEndDate: (date) => endOfMonth(date),
  getWeekDay: (date) => getDay(date),
  getYear: (date) => getYear(date),
  getMonth: (date) => getMonth(date),
  getDate: (date) => getDate(date),
  getHour: (date) => getHours(date),
  getMinute: (date) => getMinutes(date),
  getSecond: (date) => getSeconds(date),
  getMillisecond: (date) => date.getMilliseconds(),

  addYear: (date, diff) => addYears(date, diff),
  addMonth: (date, diff) => addMonths(date, diff),
  addDate: (date, diff) => addDays(date, diff),
  setYear: (date, year) => setYear(date, year),
  setMonth: (date, month) => setMonth(date, month),
  setDate: (date, num) => setDate(date, num),
  setHour: (date, hour) => setHours(date, hour),
  setMinute: (date, minute) => setMinutes(date, minute),
  setSecond: (date, second) => setSeconds(date, second),
  setMillisecond: (date, millisecond) => setMilliseconds(date, millisecond),

  isAfter: (date1, date2) => date1 > date2,
  isValidate: (date) => isValid(date),

  locale: {
    getWeekFirstDay: () => {
      return ja.options?.weekStartsOn ?? 0
    },
    getWeekFirstDate: (_locale, date) => {
      return startOfWeek(date, { locale: ja })
    },
    getWeek: (_locale, date) => {
      return getWeek(date, { locale: ja })
    },
    getShortWeekDays: () => {
      return Array.from({ length: 7 }).map((_, i) =>
        ja.localize?.day(i as Day, { width: 'short' })
      )
    },
    getShortMonths: () => {
      return Array.from({ length: 12 }).map((_, i) =>
        ja.localize?.month(i as Day, { width: 'abbreviated' })
      )
    },
    format: (_locale, date, format) => {
      if (!isValid(date)) {
        return ''
      }
      return formatDate(date, localeParse(format), {
        locale: ja
      })
    },
    parse: () => null // Calendar では parse は使用しない
  }
})

// 型を明示的にエクスポート
export const Calendar = DateFnsCalendar

export type CalendarProps<DateType = Date> = AntdCalendarProps<DateType>
